/**
 * Shared chunked-upload helpers.
 *
 * Every uploader in the app slices a file into chunks, POSTs them to a
 * store/chunk endpoint, then calls a combine/chunk endpoint to reassemble them
 * on S3. This holds the one copy of the slicing, concurrency and progress
 * logic; each page still owns its own axios calls and combine handling.
 */

// CHUNK_SIZE must stay >= 5MB. The backend concatenates chunks with S3's
// uploadPartCopy, which requires every part except the last to be at least
// that big; smaller chunks fall back to a much slower server-side path.
// It must also stay under nginx's client_max_body_size and PHP's
// upload_max_filesize / post_max_size, or every chunk is rejected with a 413.
//
// Bytes held in browser memory at once is CHUNK_SIZE * CHUNK_CONCURRENCY.
// At 10MB x 4 that is 40MB; raising either multiplies it, and a large enough
// product can crash the renderer tab outright.
export const CHUNK_SIZE = 10 * 1024 * 1024;

// Chunks in flight at once. Each one occupies a PHP-FPM worker for the whole
// request, so raising this is bounded by pm.max_children on the server.
export const CHUNK_CONCURRENCY = 4;

// How often the progress callback may fire, in ms. Without this, parallel
// chunks produce hundreds of progress events a second.
const PROGRESS_THROTTLE_MS = 200;

/**
 * Slice a file and upload its chunks with a bounded worker pool.
 *
 * @param {File}     file         The file to upload.
 * @param {Function} uploadChunk  async (chunk, index, onUploadProgress) => any.
 *                                index is 1-based. Should throw on failure;
 *                                retries are the caller's responsibility.
 * @param {Function} [onProgress] Called with an integer percentage 0-99.
 * @param {number}   [chunkSize]
 * @param {number}   [concurrency]
 * @returns {Promise<number>} The number of chunks uploaded, for the combine call.
 */
export const uploadFileInChunks = async ({
    file,
    uploadChunk,
    onProgress,
    chunkSize = CHUNK_SIZE,
    concurrency = CHUNK_CONCURRENCY,
}) => {
    const totalChunks = Math.max(1, Math.ceil(file.size / chunkSize));

    // Bytes confirmed sent per chunk. Chunks finish out of order once they run
    // in parallel, so progress is the sum of this array rather than a counter.
    const loadedPerChunk = new Array(totalChunks).fill(0);

    let lastPaintedAt = 0;

    const paintProgress = (force) => {
        if (!onProgress) {
            return;
        }

        const now = Date.now();

        if (!force && now - lastPaintedAt < PROGRESS_THROTTLE_MS) {
            return;
        }

        lastPaintedAt = now;

        const loaded = loadedPerChunk.reduce((sum, bytes) => sum + bytes, 0);

        onProgress(Math.min(99, Math.round((loaded * 100) / file.size)));
    };

    let nextChunkIndex = 0;
    let uploadError = null;

    const runWorker = async () => {
        while (true) {
            if (uploadError) {
                return;
            }

            const i = nextChunkIndex++;

            if (i >= totalChunks) {
                return;
            }

            const start = i * chunkSize;
            const end = Math.min(file.size, start + chunkSize);
            const chunk = file.slice(start, end);
            const chunkBytes = end - start;

            try {
                await uploadChunk(chunk, i + 1, (progressEvent) => {
                    // progressEvent.loaded includes multipart overhead, so clamp
                    // to the real chunk size. Overwriting rather than adding
                    // keeps a retried attempt from double counting.
                    loadedPerChunk[i] = Math.min(progressEvent.loaded, chunkBytes);
                    paintProgress(false);
                });

                loadedPerChunk[i] = chunkBytes;
                paintProgress(false);
            } catch (error) {
                uploadError = error;

                return;
            }
        }
    };

    const workerCount = Math.min(concurrency, totalChunks);

    await Promise.all(Array.from({ length: workerCount }, () => runWorker()));

    if (uploadError) {
        throw uploadError;
    }

    paintProgress(true);

    return totalChunks;
};
